import React, { useState, useRef } from "react";

import MainButton from "../../../components/main-button";
import { MainTableContainer } from "../../../components/main-table/main-table-style";
import BodyText from "../../../components/components-text/body-text";
import LeftInfoModal from "../../../components/left-info-modal";
import { PrivilegesUploadContainer } from "./privileges-upload-style";
import { images } from "../../../assets";

import PrivilegesServices from "../../../services/PrivilegesServices";
import { withGeneral } from "../../../context/mixin/with-general";
import { validateFile, documentTableColumns, fileAccept } from "./utils";
import { csvExcelBlob, downloadBlob, getFileSize } from "../../../utils/utility";
import { getText } from "../../../utils/literales";
import { PrivilegesUploadProps, ResponseModal } from "./privileges-upload-dto";
import { FileContent } from "../../../services/PrivilegesServices-dto";

const PrivilegesUpload: React.FC<PrivilegesUploadProps> = ({ setLoading, literales }) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileToUpload, setFileToUpload] = useState<File | null>(null);
  const [isFileToRemove, setIsFileToRemove] = useState(false);
  const [fileErrors, setFileErrors] = useState<string[]>([]);
  const [uploadResponse, setUploadResponse] = useState<ResponseModal | null>(null);
  const [resultFile, setResultFile] = useState<FileContent | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSelectFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || event.target.files.length <= 0) return;

    const file = event.target.files[0];
    setSelectedFile(file);
    setResultFile(null);
    // Necesario para que en Chrome se pueda volver a cargar un archivo con el mismo nombre
    if (fileInputRef && fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleAddFile = async () => {
    if (!selectedFile) return;
    const errors = await validateFile(selectedFile);

    if (errors.length <= 0) {
      setFileToUpload(selectedFile);
      setSelectedFile(null);
    } else {
      setFileErrors(errors);
    }
  };

  const handleUploadFile = async () => {
    setResultFile(null);
    if (!fileToUpload) return;

    const response = await PrivilegesServices.updatePrivilegesByFile({
      file: fileToUpload,
      setLoading,
      loader: true,
    });

    if ("error" in response) {
      setUploadResponse({
        type: "error",
        mainTitle: "Error",
        mainText: response.message,
        className: "error",
      });
      return;
    }

    if (response.ok) {
      setUploadResponse({
        type: "info",
        mainTitle: "Aviso",
        mainText:
          response.result === "exit"
            ? getText(
                literales,
                "privileges-200",
                "La carga de privilegios se ha completado correctamente."
              )
            : getText(
                literales,
                "privileges-206",
                "La carga de privilegios se ha completado con errores."
              ),
        className: response.result === "exit" ? "info" : "info red-text",
      });
      setResultFile(response.data);
      setFileToUpload(null);
    }
  };

  const downloadResultFile = () => {
    if (!resultFile) return;

    const blob = csvExcelBlob(resultFile.content);
    downloadBlob(blob, `${resultFile.metadata.name || "resultados_actualizacion_privilegios"}`);
    cleanForm();
    setUploadResponse(null);
  };

  const cleanForm = () => {
    setSelectedFile(null);
    setFileToUpload(null);
  };

  return (
    <PrivilegesUploadContainer>
      <h3>Carga de Nuevos Privilegios</h3>

      <div className="privileges">
        <div className="privileges-data">
          <div className="colSin-100 topMargin20">
            <div className="colSin-60 rightPadding20">
              <div className="inputBox custom-file-upload">
                <div className="colSin-70 required">
                  <input type="text" readOnly value={selectedFile ? selectedFile.name : ""} />
                </div>

                <div className="colSin-30">
                  <label className="custom-file-upload-button topMargin5">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept={fileAccept}
                      onChange={handleSelectFile}
                    />
                    <MainButton onClick={() => true} text="Buscar" />
                  </label>
                </div>

                <label>Adjuntar fichero</label>
              </div>
            </div>

            <div className="colSin-40 rightPadding20 topMargin15">
              <label className="custom-file-upload">
                <div className="upload-buttons">
                  <MainButton
                    text="Adjuntar Documento"
                    type={selectedFile ? "" : "disabled"}
                    icon
                    iconImg={images.IconUploadBlue}
                    disabled={selectedFile ? false : true}
                    onClick={handleAddFile}
                  />
                </div>
              </label>
            </div>
          </div>

          <div className="colSin-100 topMargin20">
            {fileToUpload && (
              <>
                <MainTableContainer className="table noZebra uploadedTable">
                  <thead>
                    <tr>
                      {documentTableColumns.map((column, index) => (
                        <th key={`column-${column}`}>
                          <BodyText className="dimmed-mini-title">
                            <p>{column}</p>
                          </BodyText>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="main-table-row">
                      <td>
                        <BodyText className="dimmed-mini-body">
                          <p>{fileToUpload.name}</p>
                        </BodyText>
                      </td>
                      <td className="action-buttons">
                        <div className="uploadedFileTable">
                          <MainButton
                            icon
                            iconImg={images.IconDeleteWhite}
                            onClick={() => setIsFileToRemove(true)}
                          />
                        </div>
                      </td>
                      <td>
                        <BodyText className="dimmed-mini-body">
                          <p>{getFileSize(fileToUpload.size)}</p>
                        </BodyText>
                      </td>
                    </tr>
                  </tbody>
                </MainTableContainer>

                <p className="text-max-size">
                  * El tamaño máximo del fichero a adjuntar es de 2MB.
                </p>
              </>
            )}
          </div>
        </div>

        <div className="privileges-action">
          <MainButton
            onClick={handleUploadFile}
            text="Ejecutar proceso de alta"
            disabled={fileToUpload ? false : true}
          />
        </div>

        <LeftInfoModal
          img={images.IconInfoBlue}
          className="info"
          mainTitle="¿Quieres eliminar este archivo?"
          buttonText="Aceptar"
          onClickAccept={cleanForm}
          close={() => setIsFileToRemove(false)}
          active={isFileToRemove}
        />

        <LeftInfoModal
          img={images.IconInfoBlue}
          className="info"
          mainTitle="Aviso"
          buttonText="Aceptar"
          onClickAccept={() => {}}
          close={() => {
            setFileErrors([]);
            cleanForm();
          }}
          active={fileErrors.length > 0}
          list={fileErrors}
          noSecondaryButton
          centerPrimaryButton
        />

        {uploadResponse && uploadResponse.type && (
          <LeftInfoModal
            img={uploadResponse.type === "info" ? images.IconInfoBlue : images.IconWarningRed}
            className={uploadResponse.className}
            mainTitle={uploadResponse.mainTitle}
            mainText={uploadResponse.mainText}
            buttonText={
              uploadResponse.type === "info"
                ? getText(
                    literales,
                    "privileges-download-button",
                    "Descargar documento de resultados"
                  )
                : "Aceptar"
            }
            onClickAccept={
              uploadResponse.type === "info"
                ? downloadResultFile
                : () => {
                    setUploadResponse(null);
                    cleanForm();
                  }
            }
            active={uploadResponse.mainTitle}
            noSecondaryButton
            centerPrimaryButton
            hasAcceptNoClose
          />
        )}
      </div>
    </PrivilegesUploadContainer>
  );
};

export default withGeneral(PrivilegesUpload);
